import { BetValidationErrors } from '../../../../../controllers/gameCommunication/types';
import type { CoreDispatch } from '../../../../../store/CoreStore';
import { autoplayActions } from '../../../../../store/slices/autoplay/slice';
import { betActions } from '../../../../../store/slices/bets/slice';
import type { AcceptedBet, BetType } from '../../../../../store/slices/bets/types';
import { toastActions } from '../../../../../store/slices/toast/slice';

interface ValidateAndSaveBetsProps {
  dispatch: CoreDispatch;
  currentBet: BetType;
  /**
   * Array of accepted bets that were resolved or are in the process of being resolved by the server.
   */
  acceptedBets: AcceptedBet[];
  /**
   * Array of invalid betspots that were found during local validation.
   */
  invalidBets: string[];
}

enum ValidateType {
  None,
  Local,
  AcceptedBets,
}

/**
 * This function is used to validate and save autoplay bets.
 * If acceptedBets are available, it uses them as a source of truth.
 * If not, it uses local validation to remove invalid bets.
 * If no invalid bets are found, it just saves the current bet.
 * If invalid bets are found - they are removed from the current bet and a toast is shown
 * informing the user that not all the bets were saved.
 */
export function validateAndSaveBets(props: ValidateAndSaveBetsProps): void {
  const { dispatch, currentBet, acceptedBets, invalidBets } = props;
  const betsToSave: BetType = { betSpots: {} };
  // We need to check each bet as they are resolved one by one.
  const hasUnresolvedBets = acceptedBets.length > 0 && !!acceptedBets.find((bet) => bet.errorCode === -1);

  // We prioritize accepted bets over local validation.
  const localValidationMethod = invalidBets.length > 0 ? ValidateType.Local : ValidateType.None;
  const validateWith =
    !hasUnresolvedBets && acceptedBets.length > 0 ? ValidateType.AcceptedBets : localValidationMethod;

  switch (validateWith) {
    case ValidateType.AcceptedBets:
      saveResolvedBets(dispatch, acceptedBets);
      break;
    // If bets are not accepted yet - use local validation to remove invalid bets.
    case ValidateType.Local:
      saveLocallyValidatedBets(dispatch, currentBet, invalidBets);
      break;
    default:
      /**
       * If the bets are not accepted yet and local validation has not found any invalid bets,
       * then just save the current bet.
       */

      betsToSave.betSpots = currentBet.betSpots;
      dispatch(autoplayActions.saveBet(betsToSave));
      break;
  }
}

function saveResolvedBets(dispatch: CoreDispatch, acceptedBets: AcceptedBet[]): void {
  const betsToSave: BetType = { betSpots: {} };
  let someBetsInvalid = false;
  for (const bet of acceptedBets) {
    if (
      !(
        bet.errorCode === BetValidationErrors.DidntMeetMinimumTableLimit ||
        bet.errorCode === BetValidationErrors.BetNotAllowed
      )
    ) {
      betsToSave.betSpots[bet.betCode] = {
        ...bet,
        value: bet.amount,
        type: bet.type,
        count: bet.count,
      };
    } else {
      // No need to wipe bets manually here as they are wiped at the BetsAccepted stage.
      someBetsInvalid = true;
    }
  }
  if (someBetsInvalid) {
    dispatch(toastActions.createToast('AUTOPLAY_NOT_ALL_BETS_SAVED'));
  }
  dispatch(autoplayActions.saveBet(betsToSave));
}

function saveLocallyValidatedBets(dispatch: CoreDispatch, currentBet: BetType, invalidBets: string[]): void {
  const betsToSave: BetType = { betSpots: {} };
  Object.keys(currentBet.betSpots).forEach((betSpot) => {
    if (!invalidBets.includes(betSpot)) {
      const betObj = currentBet.betSpots[betSpot]!;
      betsToSave.betSpots[betSpot] = {
        value: betObj.value,
        type: betObj.type,
        count: betObj.count,
      };
    } else {
      dispatch(betActions.wipeBet(parseInt(betSpot, 10)));
    }
  });
  dispatch(autoplayActions.saveBet(betsToSave));
  dispatch(toastActions.createToast('AUTOPLAY_NOT_ALL_BETS_SAVED')); // If this function is called, it means that not all bets are valid therefore we dispatch this.
}
